import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import * as d3 from 'd3';
import { useAuth } from '../../../contexts/AuthContext';
import { usePrivacy } from '../../../contexts/PrivacyContext';

const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
};

const calculateDurationMinutes = (startTime, endTime, isCrossDate) => {
    if (!startTime || !endTime) return 0;

    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);

    if (startMinutes === endMinutes) return 0;

    let durationMinutes;
    if (isCrossDate) {
        durationMinutes = (1440 - startMinutes) + endMinutes;
    } else {
        durationMinutes = endMinutes > startMinutes ? endMinutes - startMinutes : 0;
    }

    return durationMinutes;
};

const minutesToHours = (minutes) => {
    return minutes / 60;
};

const MemberParticipationChart = ({ activitiesData, mode = "self" }) => {
    const svgRef = useRef(null);
    const containerRef = useRef(null);
    const [showLegend, setShowLegend] = useState(false);
    const { groupMembers } = useAuth();
    const { maskName } = usePrivacy();

    useEffect(() => {
        if (!activitiesData || activitiesData.length === 0 || !svgRef.current || !containerRef.current) return;

        d3.select(svgRef.current).selectAll("*").remove();
        d3.select(containerRef.current).selectAll(".member-participation-tooltip").remove();

        const containerRect = containerRef.current.getBoundingClientRect();
        const width = containerRect.width;
        const height = containerRect.height;

        // to ensure the labels are within the container
        const margin = 30;
        const maxRadius = Math.min((width - margin) / 2.5, (height - margin) / 2.5);
        const radius = maxRadius * 0.75; // radius of the pie chart
        const labelRadius = maxRadius * 0.95; // label radius to ensure it's within the container

        const memberParticipation = {};

        const memberColorMap = {};
        if (groupMembers && groupMembers.length > 0) {
            groupMembers.forEach(member => {
                if (member.email && member.color) {
                    memberColorMap[member.email] = member.color;
                }
            });
        }

        activitiesData.forEach(activity => {
            if (!activity.contributors || !Array.isArray(activity.contributors)) return;

            const durationMinutes = calculateDurationMinutes(
                activity.startTime,
                activity.endTime,
                activity.isCrossDate || false
            );

            const hours = minutesToHours(durationMinutes);

            if (hours <= 0) return;

            const contributors = activity.contributors;
            const count = contributors.length;

            if (count === 0) return;

            contributors.forEach(contributor => {
                if (contributor.email) {
                    const email = contributor.email;
                    const name = maskName(contributor.name || email.split('@')[0]);

                    // Build a 2-letter initial from group member name if possible
                    let initials = '';
                    if (name) {
                        const parts = name.trim().split(/\s+/);
                        if (parts.length === 1) {
                            initials = parts[0].slice(0, 2).toUpperCase();
                        } else {
                            const first = parts[0].charAt(0);
                            const last = parts[parts.length - 1].charAt(0);
                            initials = (first + last).toUpperCase();
                        }
                    } else {
                        initials = email.slice(0, 2).toUpperCase();
                    }

                    if (!memberParticipation[email]) {
                        memberParticipation[email] = {
                            name,          // full display name
                            initial: initials, // 2-letter initial for x-axis
                            self: 0,
                            pair: 0,
                            team: 0,
                            color: memberColorMap[email] || '#4f46e5'
                        };
                    }

                    if (count === 1) memberParticipation[email].self += hours;
                    else if (count === groupMembers.length) memberParticipation[email].team += hours;
                    else memberParticipation[email].pair += hours;
                }
            });
        });

        const data = Object.values(memberParticipation);

        // Sort members alphabetically by last name (then first name)
        data.sort((a, b) => {
            const aParts = (a.name || '').trim().split(/\s+/);
            const bParts = (b.name || '').trim().split(/\s+/);
            const aLast = aParts.length ? aParts[aParts.length - 1].toLowerCase() : '';
            const bLast = bParts.length ? bParts[bParts.length - 1].toLowerCase() : '';
            if (aLast !== bLast) return aLast.localeCompare(bLast);
            const aFirst = aParts.length ? aParts[0].toLowerCase() : '';
            const bFirst = bParts.length ? bParts[0].toLowerCase() : '';
            return aFirst.localeCompare(bFirst);
        });

        // Only keep members that have > 0 hours for the current mode
        const modeData = data.filter(d => (d[mode] || 0) > 0);

        if (modeData.length === 0) {
            d3.select(svgRef.current)
                .attr("viewBox", `0 0 ${width} ${height}`)
                .attr("preserveAspectRatio", "xMidYMid meet")
                .style("width", "100%")
                .style("height", "100%")
                .append("text")
                .attr("x", width / 2)
                .attr("y", height / 2)
                .attr("text-anchor", "middle")
                .style("font-size", "14px")
                .text("No participation data for this mode");
            return;
        }

        const svg = d3.select(svgRef.current)
            .attr("viewBox", `0 0 ${width} ${height}`)
            .append("g")
            .attr("transform", `translate(10,16)`);

        const chartWidth = width - 20;
        const chartHeight = height - 32;

        const x = d3.scaleBand()
            .domain(modeData.map(d => d.name))
            .range([0, chartWidth])
            .padding(0.3);

        const y = d3.scaleLinear()
            .domain([0, d3.max(modeData, d => d[mode] || 0)])
            .nice()
            .range([chartHeight, 0]);

        svg.append("g")
            .attr("transform", `translate(0,${chartHeight})`)
            .call(
                d3.axisBottom(x).tickFormat(nameValue => {
                    const member = modeData.find(d => d.name === nameValue);
                    if (member && member.initial) return member.initial;
                    return (typeof nameValue === 'string' && nameValue.charAt(0).toUpperCase()) || '';
                })
            );

        svg.append("g")
            .call(d3.axisLeft(y));
        
        const tooltip = d3.select(containerRef.current)
            .append("div")
            .attr("class", "member-participation-tooltip")
            .style("position", "absolute")
            .style("pointer-events", "none")
            .style("background", "rgba(0, 0, 0, 0.8)")
            .style("color", "#fff")
            .style("padding", "6px 10px")
            .style("border-radius", "4px")
            .style("font-size", "11px")
            .style("opacity", 0)
            .style("z-index", 10);

        const bars = svg.selectAll(".bar")
            .data(modeData)
            .enter()
            .append("rect")
            .attr("class", "bar")
            .attr("x", d => x(d.name))
            .attr("y", d => y(d[mode] || 0))
            .attr("width", x.bandwidth())
            .attr("height", d => chartHeight - y(d[mode] || 0))
            .attr("fill", d => {
                const base = d.color || '#4f46e5';
                const c = d3.color(base);
                return c && c.darker ? c.darker(0.7).toString() : base;
            })
            .attr("fill-opacity", 0.9)
            .on("mouseover", (event, d) => {
                const hours = d[mode] || 0;
                const rectBounds = containerRef.current.getBoundingClientRect();
                tooltip
                    .style("opacity", 1)
                    .style("left", `${event.clientX - rectBounds.left}px`)
                    .style("top", `${event.clientY - rectBounds.top - 16}px`)
                    .html(`<strong>${d.name}</strong><br/>${hours.toFixed(2)} hours`);
                d3.select(event.currentTarget)
                    .attr("fill-opacity", 1)
                    .attr("stroke", "#111")
                    .attr("stroke-width", 1);
            })
            .on("mousemove", (event) => {
                const rectBounds = containerRef.current.getBoundingClientRect();
                tooltip
                    .style("left", `${event.clientX - rectBounds.left}px`)
                    .style("top", `${event.clientY - rectBounds.top - 16}px`);
            })
            .on("mouseout", (event) => {
                tooltip.style("opacity", 0);
                d3.select(event.currentTarget)
                    .attr("fill-opacity", 0.9)
                    .attr("stroke", "none");
            });

        bars.append("title")
            .text(d => {
                const hours = d[mode] || 0;
                return `${d.name}: ${hours.toFixed(2)} hours`;
            });


    }, [activitiesData, groupMembers, mode, maskName]);

    const titleMap = {
        self: "Self Work Hours",
        pair: "Pair Work Hours",
        team: "Team Work Hours"
    };

    const legendDescriptionMap = {
        self: "Total hours spent working individually.",
        pair: "Total hours spent working with at least one but not all group members.",
        team: "Total hours spent working with the entire team."
    };

    return (
        <ChartContainer ref={containerRef}>
            <TitleContainer>
                <Title>
                    {titleMap[mode]}
                    <InfoIconContainer
                        onMouseEnter={() => setShowLegend(true)}
                        onMouseLeave={() => setShowLegend(false)}
                    >
                        <InfoIcon>ⓘ</InfoIcon>
                        {showLegend && (
                            <LegendTooltip>
                                <LegendTitle>Member Participation Bar Chart</LegendTitle>
                                <LegendItem>
                                    <LegendText>
                                        {legendDescriptionMap[mode]}
                                    </LegendText>
                                </LegendItem>
                                <LegendItem>
                                    <LegendSymbol>
                                        <div style={{
                                            width: '18px',
                                            height: '18px',
                                            background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4, #ffd93d, #6bcf7f)',
                                            borderRadius: '3px'
                                        }}></div>
                                    </LegendSymbol>
                                    <LegendText>
                                    Bar color matches the member&apos;s profile color.
                                    </LegendText>
                                </LegendItem>
                                <LegendItem>
                                    <LegendSymbol>
                                        <div style={{
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                            color: '#333',
                                            border: '1px solid #ccc',
                                            padding: '2px 6px',
                                            margin: '8px 8px 8px 12px',   // top, right, bottom, left
                                            borderRadius: '3px',
                                            backgroundColor: '#f5f5f5'
                                        }}>
                                            AB<br />7.5 hours
                                        </div>
                                    </LegendSymbol>
                                    <LegendText>
                                        X-axis show initials. Hover a bar to see the full name and exact hours.
                                    </LegendText>
                                </LegendItem>
                                
                            </LegendTooltip>
                        )}
                    </InfoIconContainer>
                </Title>
            </TitleContainer>
            <SVG ref={svgRef} />
        </ChartContainer>
    );
};

const ChartContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: visible;
    min-height: 0;
    flex: 1;
    padding: 0 0 8px 0;
    box-sizing: border-box;
`;

const TitleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2px;
    flex-shrink: 0;
    height: 24px;
`;

const Title = styled.h3`    font-size: 16px;
    margin: 0;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
`;

const InfoIconContainer = styled.div`
    position: relative;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const InfoIcon = styled.div`
    font-size: 14px;
    color: #666;
    background-color: #f5f5f5;
    border: 1px solid #ccc;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #e0e0e0;
        border-color: #999;
        color: #333;
    }
`;

const LegendTooltip = styled.div`
    position: absolute;
    top: 26px;
    right: 0;
    background-color: #fff;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 15px;
    width: 320px;
    z-index: 9999;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const LegendTitle = styled.h4`
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 12px 0;
    color: #333;
    text-align: center;
    border-bottom: 1px solid #eee;
    padding-bottom: 8px;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
    &:last-child {
        margin-bottom: 0;
    }
`;

const LegendSymbol = styled.div`
    width: 20px;
    height: 20px;
    margin-right: 12px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const LegendText = styled.div`
    font-size: 12px;
    color: #555;
    text-align: center;
    line-height: 1.3;
`;

const SVG = styled.svg`
    width: 100%;
    height: 100%;
    flex: 1;
    min-height: 0;
    overflow: visible;
    display: block;
`;

export default React.memo(MemberParticipationChart); 
